/**
 * Занятость конвейера: один ответ на двух читателей (S48).
 *
 * ## Почему это отдельный модуль
 *
 * Признак жил внутри `PipelineBar` и был нужен только ему: полоса конвейера
 * решает по нему, писать «идёт» или «готово». Второй читатель появился, когда
 * выяснилось, чем оборачивается его отсутствие на вкладке «Проверка». Та
 * смотрит только на прогоны правил, и пока конвейер разбирал 153 документа
 * боевой папки, она двадцать четыре минуты советовала нажать «2. Распознать» —
 * действие, которое не нужно и, если его выполнить, начинает всё заново.
 *
 * Копия признака во второй вкладке разошлась бы с первой при первом же
 * уточнении списка стадий, и разошлась бы молча: обе фразы правдоподобны.
 */
import type { ProcessingStatus } from '../../api/types.js';

/**
 * Стадии, на которых конвейер что-то делает прямо сейчас, — В ПОРЯДКЕ конвейера.
 *
 * Порядок значим для второго читателя (`state.ts`): из нескольких ждущих стадий
 * называется самая ранняя, потому что она и держит остальные.
 */
export const PIPELINE_STAGES: readonly string[] = [
  'uploaded',
  'layout',
  'recognition',
  'analysis',
  'checks',
];

/**
 * Занят ли конвейер.
 *
 * Очередь считается занятостью наравне с выполнением: задача, ждущая
 * исполнителя, — это работа, которая идёт, просто ещё не начатая. Прежнее
 * условие смотрело только на `running`, и время ожидания воркера экран
 * показывал как простой.
 *
 * ## Почему мало одной сводной стадии
 *
 * Сводная стадия при единственной мёртвой задаче становится `failed` целиком
 * (`summaryStage`), и по ней занятость читается как «не занят» — вместе со
 * всем, что от неё зависит. На боевой папке это стоило кнопки «Стоп»:
 * `doc.match_registry` умерла на ограничении БД, а распознавание в ту же
 * секунду шло, 38 страниц из 220, — и остановить его с экрана было нечем.
 * Труп прошлого шага не делает идущую работу законченной, поэтому занятость
 * спрашивает ещё и постадийные счётчики: `pending` считает очередь и
 * выполнение по стадиям и отказом не гасится.
 */
export function isBusy(status: ProcessingStatus | null | undefined): boolean {
  if (status === undefined || status === null) return false;
  if (status.queued === 0 && status.running === 0) return false;
  if (PIPELINE_STAGES.includes(status.stage)) return true;
  return activeStageOf(status) !== null;
}

/**
 * Какая кнопка снятия нужна папке — и нужна ли (S54).
 *
 * `stop` — работа идёт, и человек её прекращает. `reset` — работа НЕ идёт, но
 * от неё остался мусор: мёртвые задачи и прогон, который никто не закроет.
 * `null` — снимать нечего.
 *
 * Второе состояние появилось не в теории. На боевой папке после отказа
 * `doc.match_registry` и снятия финализатора конвейер встал так: очередь
 * пуста, прогон распознавания навсегда «идёт», мёртвая задача держит свой
 * `dedupe_key` и молча гасит следующую цепочку. Разобрать этот узел с карточки
 * папки было нечем — только консолью задач в администрировании, где нужную
 * строку ещё надо найти среди чужих.
 *
 * Обе кнопки зовут ОДНУ ручку `POST /pipeline/folders/:id/stop`: она снимает
 * задачи стадий распознавания, разбора и проверки в любом состоянии, включая
 * мёртвые, и закрывает незавершённый прогон. Разные только слова, потому что
 * человеку это два разных поступка.
 */
export type ResetControl = 'stop' | 'reset' | null;

export function resetControl(
  status: ProcessingStatus | null | undefined,
  hasRunningRun: boolean,
): ResetControl {
  if (isBusy(status)) return 'stop';
  if ((status?.dead ?? 0) > 0) return 'reset';
  return hasRunningRun ? 'reset' : null;
}

/**
 * Стадия, которая идёт ПРЯМО СЕЙЧАС, — самая ранняя с задачами в очереди.
 *
 * `stage` сводки отвечает на другой вопрос: это самая ДАЛЬНЯЯ стадия с
 * активностью. Пока пересобирается рабочий документ, дальней остаётся разметка
 * от прошлого прогона, и по ней экран обещал бы выделение блоков, которое ещё
 * даже не поставлено в очередь. Список стадий приходит отсортированным по
 * порядку конвейера, поэтому первая ждущая в нём и есть текущая.
 */
export function activeStageOf(status: ProcessingStatus | null | undefined): string | null {
  return (status?.stages ?? []).find((summary) => summary.pending > 0)?.stage ?? null;
}

/**
 * Идёт ли работа, которая САМА закончится проверкой.
 *
 * Отвечает на вопрос вкладки «Проверка»: ждать ли ей результата или звать
 * человека. Распознавание сюда входит наравне с анализом — цепочка от него до
 * прогона правил идёт без участия человека, если запуск был сквозным.
 */
export function checksAhead(status: ProcessingStatus | null | undefined): boolean {
  if (status === undefined || status === null) return false;
  if (!isBusy(status)) return false;
  const now = activeStageOf(status) ?? status.stage;
  return now === 'recognition' || now === 'analysis' || now === 'checks';
}
